// src/components/Utils/MDEditor.js
import React, { useState, useEffect, useCallback } from 'react'; // useCallback 추가
import { ArrowLeft, ChevronLeft, Download, Copy, Check, Eye, EyeOff, RotateCcw, FileCode, Maximize2, Minimize2, Volume2, VolumeX, X } from 'lucide-react';
import './MDEditor.css';
import './common_Utils.css';
import { downloadAsHTML } from './MDtoHTML'; // HTML 변환 함수 import
import ttsManager from './TTS';

const MDEditor = ({ onBack, user, userProfile, initialFileContent }) => { 
  const defaultContent = '# Markdown 편집기에 오신 것을 환영합니다!\n\n여기에 **Markdown** 문법을 사용하여 작성하세요.\n\n## 주요 기능\n- 실시간 미리보기\n- 파일 다운로드\n- 클립보드 복사\n\n### 예제 코드\n```javascript\nconst hello = "World";\nconsole.log(hello);\n```\n\n> 이것은 인용문입니다.\n\n- 항목 1\n- 항목 2\n- 항목 3';

  // initialFileContent가 있으면 그 값을 사용하고, 없으면 기본값을 사용
  const [markdown, setMarkdown] = useState(initialFileContent || defaultContent);
  const [copied, setCopied] = useState(false);
  const [frontPane, setFrontPane] = useState('editor');
  const [fullScreen, setFullScreen] = useState(true);
  const [isDragging, setIsDragging] = useState(false); // 드래그 상태 추가
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const [activeTab, setActiveTab] = useState('editor');

  // 드래그 이동 관련 상태
  const [editorPosition, setEditorPosition] = useState({ x: 0, y: 0 });
  const getInitialPreviewPosition = () => {
    // 에디터 컨테이너의 너비를 기준으로 50% 계산
    const containerWidth = window.innerWidth;
      return { x: containerWidth * 0.5, y: 0 };
    };

  const [previewPosition, setPreviewPosition] = useState(getInitialPreviewPosition());
  // const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [draggingPane, setDraggingPane] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // 팝업 창 관련
  const [editorPopup, setEditorPopup] = useState(null);
  const [previewPopup, setPreviewPopup] = useState(null);

  // Markdown을 HTML로 변환 (간단한 구현)
  const parseMarkdown = (text) => {
    let html = text;
    
    // 코드 블록
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
      return `<pre><code class="language-${lang || 'plaintext'}">${escapeHtml(code.trim())}</code></pre>`;
    });
    
    // 인라인 코드
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // 헤더
    html = html.replace(/^#### (.*$)\n?/gim, '<h4>$1</h4>'); // <-- \n? 추가
    html = html.replace(/^### (.*$)\n?/gim, '<h3>$1</h3>');  // <-- \n? 추가
    html = html.replace(/^## (.*$)\n?/gim, '<h2>$1</h2>');   // <-- \n? 추가
    html = html.replace(/^# (.*$)\n?/gim, '<h1>$1</h1>');    // <-- \n? 추가
    
    // 굵게 
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    html = html.replace(/^[\t ]*- (?!.*<strong|<\/strong>)(.*)$/gim, '&nbsp;&nbsp;&nbsp; &middot $1');
    
    // 기울임
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    // html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
    
    // 인용문
    html = html.replace(/^> (.*)$/gim, '<blockquote>$1</blockquote>');
    
    // 링크
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
    
    // 이미지
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
    
    // 리스트
    // html = html.replace(/^\- (.*)$/gim, '<li>$1</li>'); 
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    // html = html.replace(/^\- (.*)$/gim, '<span>&middot; $1</span>');
    
    // 순서 있는 리스트
    // html = html.replace(/^\d+\. (.*)$/gim, '<li>$1</li>');
 
    
    // 줄바꿈
    html = html.replace(/\n/g, '<br>');
    
    return html;
  };

  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([markdown], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = 'document.md';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  // HTML 파일로 다운로드하는 함수
  const handleDownloadHTML = () => {
    const filename = 'document.html';
    downloadAsHTML(markdown, filename);
  };

  // 파일 내용을 읽어 상태를 업데이트하는 공통 함수
  const readFileContent = (file) => {
    // 파일 확장자/타입 검사 (선택적)
    if (file.type && !['text/markdown', 'text/plain'].includes(file.type) && !file.name.endsWith('.md') && !file.name.endsWith('.markdown') && !file.name.endsWith('.txt')) {
      alert('Markdown (.md, .markdown) 또는 텍스트 (.txt) 파일만 열 수 있습니다.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setMarkdown(e.target.result);
    };
    reader.readAsText(file);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      readFileContent(file);
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    if (window.confirm('편집기를 초기화하시겠습니까? 현재 내용은 모두 삭제됩니다.')) {
      setMarkdown(defaultContent);
    }
  };

  // Markdown에서 텍스트 추출 함수
  const extractTextFromMarkdown = (markdown) => {
    return markdown
      .replace(/```[\s\S]*?```/g, '') // 코드 블록 제거
      .replace(/`[^`]+`/g, '') // 인라인 코드 제거
      .replace(/#{1,6}\s/g, '') // 헤더 기호 제거
      .replace(/\*\*([^*]+)\*\*/g, '$1') // 굵게 제거
      .replace(/__([^_]+)__/g, '$1') // 굵게 제거
      .replace(/\*([^*]+)\*/g, '$1') // 기울임 제거
      .replace(/_([^_]+)_/g, '$1') // 기울임 제거
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 링크 제거
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // 이미지 제거
      .replace(/^>\s/gm, '') // 인용문 기호 제거
      .replace(/^[-*]\s/gm, '') // 리스트 기호 제거
      .trim();
  };

  // Text-to-Speech 핸들러
  const handleTextToSpeech = () => {
    const plainText = extractTextFromMarkdown(markdown);
    const success = ttsManager.speak(
      plainText,
      () => setIsSpeaking(true),  // onStart
      () => setIsSpeaking(false), // onEnd
      (error) => console.error('TTS 오류:', error) // onError
    );
    
    // 이미 실행 중이었다면 중지된 것이므로 상태 업데이트
    if (!success && isSpeaking) {
      setIsSpeaking(false);
    }
  };

  // 컴포넌트 언마운트 시 음성 중지
  useEffect(() => {
    return () => {
      ttsManager.stop();
    };
  }, []);

  // 팝업 창 닫힐 때 정리
  useEffect(() => {
    return () => {
      if (editorPopup && !editorPopup.closed) {
        editorPopup.close();
      }
      if (previewPopup && !previewPopup.closed) {
        previewPopup.close();
      }
    };
  }, [editorPopup, previewPopup]);

  // 패인 드래그 시작
  const handlePaneDragStart = (paneType, e) => {
    if (e.target.closest('.pane-header')) {
      setDraggingPane(paneType);
      
      // 현재 패인의 위치 가져오기
      const pane = e.currentTarget.parentElement; // pane-header의 부모 요소(editor-pane 또는 preview-pane)
      const rect = pane.getBoundingClientRect();
      
      // 드래그 시작 시 다른 pane의 현재 위치도 고정
      if (paneType === 'editor') {
        // preview의 현재 위치 저장
        const previewPane = document.querySelector('.preview-pane');
        if (previewPane && (previewPosition.x === 0 && previewPosition.y === 0)) {
          const previewRect = previewPane.getBoundingClientRect();
          setPreviewPosition({ x: previewRect.left, y: previewRect.top });
        }
      } else if (paneType === 'preview') {
        // editor의 현재 위치 저장
        const editorPane = document.querySelector('.editor-pane');
        if (editorPane && (editorPosition.x === 0 && editorPosition.y === 0)) {
          const editorRect = editorPane.getBoundingClientRect();
          setEditorPosition({ x: editorRect.left, y: editorRect.top });
        }
      }
      
      // 마우스 클릭 위치와 패인의 현재 위치 차이 계산
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // 패인 드래그 중
  const handlePaneDragMove = (e) => {
    if (!draggingPane) return;
    
    // 마우스 위치에서 드래그 오프셋을 빼서 패인의 새 위치 계산
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    if (draggingPane === 'editor') {
      setEditorPosition({ x: newX, y: newY });
    } else if (draggingPane === 'preview') {
      setPreviewPosition({ x: newX, y: newY });
    }
  };

  // 패인 드래그 종료
  const handlePaneDragEnd = () => {
    setDraggingPane(null);
  };

  // 전역 마우스 이벤트 리스너
  useEffect(() => {
    if (draggingPane) {
      document.addEventListener('mousemove', handlePaneDragMove);
      document.addEventListener('mouseup', handlePaneDragEnd);
      return () => {
        document.removeEventListener('mousemove', handlePaneDragMove);
        document.removeEventListener('mouseup', handlePaneDragEnd);
      };
    }
  }, [draggingPane, dragOffset]); 

  // 편집기를 새 창으로 열기
  const openEditorInNewWindow = () => {
    if (editorPopup && !editorPopup.closed) {
      editorPopup.focus();
      return;
    }

    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'EditorWindow', `width=${width},height=${height},left=${left},top=${top}`); 
     
    
    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Markdown 편집기</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          }
          .editor-container {
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .popup-header {
            padding: 16px 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .popup-header h3 {
            margin: 0;
            font-size: 1.2rem;
          }
          .char-count {
            font-size: 0.9rem;
            opacity: 0.9;
          }
          textarea {
            flex: 1;
            padding: 20px;
            border: none;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px;
            line-height: 1.6;
            resize: none;
            outline: none;
          }
        </style>
      </head>
      <body>
        <div class="editor-container">
          <div class="popup-header">
            <h3>📝 Markdown 편집기</h3>
            <span class="char-count" id="charCount">0 글자</span>
          </div>
          <textarea id="editor" placeholder="여기에 Markdown을 작성하세요..."></textarea>
        </div>
        <script>
          const textarea = document.getElementById('editor');
          const charCount = document.getElementById('charCount');
          
          // 메인 창의 내용 가져오기
          textarea.value = window.opener.getEditorContent();
          charCount.textContent = textarea.value.length + ' 글자';
          
          // 내용 변경 시 메인 창에 동기화
          textarea.addEventListener('input', (e) => {
            window.opener.updateEditorContent(e.target.value);
            charCount.textContent = e.target.value.length + ' 글자';
          });
          
          // 메인 창에서 업데이트 받기
          window.updateContent = (content) => {
            if (textarea.value !== content) {
              textarea.value = content;
              charCount.textContent = content.length + ' 글자';
            }
          };
        </script>
      </body>
      </html>
    `);
    
    setEditorPopup(popup);
    
    // 팝업이 닫히면 상태 초기화
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        setEditorPopup(null);
        clearInterval(checkClosed);
      }
    }, 1000);
  };

  // 미리보기를 새 창으로 열기
  const openPreviewInNewWindow = () => {
    if (previewPopup && !previewPopup.closed) {
      previewPopup.focus();
      return;
    }

    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'PreviewWindow', `width=${width},height=${height},left=${left},top=${top}`); 

    // const popup = window.open('', 'PreviewWindow', 'width=800,height=600,left=920,top=100'); 
    
    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Markdown 미리보기</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          }
          .preview-container {
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .popup-header {
            padding: 16px 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .popup-header h3 {
            margin: 0;
            font-size: 1.2rem;
          }
          .preview-content {
            flex: 1;
            padding: 20px;
            overflow-y: auto;
            background: #fdfdfd;
          }
          /* Markdown 스타일 복사 */
          .preview-content h1 {
            font-size: 2rem;
            font-weight: 700;
            margin: 1.5rem 0 1rem;
            color: #1a202c;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 0.5rem;
          }
          .preview-content h2 {
            font-size: 1.6rem;
            font-weight: 600;
            margin: 1.3rem 0 0.8rem;
            color: #2d3748;
          }
          .preview-content h3 {
            font-size: 1.3rem;
            font-weight: 600;
            margin: 1rem 0 0.6rem;
            color: #4a5568;
          }
          .preview-content p {
            margin: 0.8rem 0;
            line-height: 1.7;
          }
          .preview-content code {
            background: #f7fafc;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 0.9em;
            color: #d73a49;
          }
          .preview-content pre {
            background: #2d3748;
            padding: 16px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 1rem 0;
          }
          .preview-content pre code {
            background: none;
            color: #e2e8f0;
            padding: 0;
          }
          .preview-content blockquote {
            border-left: 4px solid #667eea;
            padding-left: 16px;
            margin: 1rem 0;
            color: #4a5568;
            font-style: italic;
            background: #f7fafc;
            padding: 12px 16px;
            border-radius: 0 8px 8px 0;
          }
          .preview-content strong {
            font-weight: 700;
            color: #1a202c;
          }
          .preview-content em {
            font-style: italic;
            color: #4a5568;
          }
          .preview-content a {
            color: #667eea;
            text-decoration: none;
            font-weight: 500;
          }
          .preview-content a:hover {
            text-decoration: underline;
          }
        </style>
      </head>
      <body>
        <div class="preview-container">
          <div class="popup-header">
            <h3>👁️ Markdown 미리보기</h3>
          </div>
          <div class="preview-content" id="preview"></div>
        </div>
        <script>
          const preview = document.getElementById('preview');
          
          // 메인 창에서 HTML 가져오기
          preview.innerHTML = window.opener.getPreviewHTML();
          
          // 메인 창에서 업데이트 받기
          window.updatePreview = (html) => {
            preview.innerHTML = html;
          };
        </script>
      </body>
      </html>
    `);
    
    setPreviewPopup(popup);
    
    // 팝업이 닫히면 상태 초기화
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        setPreviewPopup(null);
        clearInterval(checkClosed);
      }
    }, 1000);
  };

  // 전역 함수로 편집기 내용 제공
  useEffect(() => {
    window.getEditorContent = () => markdown;
    window.updateEditorContent = (content) => setMarkdown(content);
    window.getPreviewHTML = () => parseMarkdown(markdown);
  }, [markdown]);

  // 팝업 창 업데이트
  useEffect(() => {
    if (editorPopup && !editorPopup.closed && editorPopup.updateContent) {
      editorPopup.updateContent(markdown);
    }
    if (previewPopup && !previewPopup.closed && previewPopup.updatePreview) {
      previewPopup.updatePreview(parseMarkdown(markdown));
    }
  }, [markdown, editorPopup, previewPopup]);

  /* Drag and Drop 핸들러 추가 */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // 이 부분에서 setIsDragging(true)를 유지할 수 있지만, Enter에서 이미 설정했으므로 생략
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // relatedTarget을 사용하여 오버레이 영역을 벗어났을 때만 해제
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false); // 드래그 상태 해제

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      readFileContent(files[0]); // 첫 번째 파일만 처리
    }
  }, []);
  /* Drag and Drop 핸들러 끝 */

  return (
    <div 
      className={`md-editor ${isDragging ? 'dragging' : ''}`} // 드래그 상태 클래스 적용
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="md-editor-header">
        <div 
          onClick={onBack}  
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div>
        <h1>Markdown 편집기</h1>
        <div className="header-actions">
          <label className="upload-btn">
            <input 
              type="file" 
              accept=".md,.markdown,.txt" 
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            파일 열기
          </label>
          <button onClick={() => setFullScreen(!fullScreen)} className="preview-toggle-btn">
            {fullScreen ? <EyeOff className="icon" /> : <Eye className="icon" />}
            {fullScreen ? 'Split View' : 'Full Screen'}
          </button>
          {/* 초기화 버튼 */}
          {/* <button onClick={handleReset} className="action-btn reset">
            <RotateCcw className="icon" />
            초기화
          </button>
          <button onClick={handleCopy} className="action-btn">
            {copied ? <Check className="icon" /> : <Copy className="icon" />}
            {copied ? '복사됨!' : '복사'}
          </button> */}
          <button onClick={handleDownload} className="action-btn">
            <Download className="icon" />
            다운로드
          </button>
          <button onClick={handleDownloadHTML} className="action-btn html-download">
            <FileCode className="icon" />
            HTML 저장
          </button>
        </div>
      </div>

      <div className="tab-buttons"> 
        <button 
          className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          📝 Editor
        </button>
        <button 
          className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => {setActiveTab('preview'); setPreviewPosition({ x: 0, y: 0 }); }}
        >
          Preview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'both' ? 'active' : ''}`}
          onClick={() => setActiveTab('both')}
        >
          Both
        </button> 
      </div>


      <div className={`md-editor-content ${fullScreen ? 'split' : 'full'}`}>
        {(activeTab === 'editor' || activeTab === 'both') && (
        <div 
          className={`editor-pane ${isEditorFullscreen ? 'fullscreen' : ''} ${draggingPane === 'editor' ? 'dragging-pane' : ''}`}
          style={{
            position: editorPosition.x !== 0 || editorPosition.y !== 0 ? 'absolute' : 'static',
            left: editorPosition.x !== 0 ? `${editorPosition.x}px` : 'auto',
            top: editorPosition.y !== 0 ? `${editorPosition.y}px` : 'auto',
            width: editorPosition.x !== 0 || editorPosition.y !== 0 ? 'calc(50% - 10px)' : 'auto',
            height: editorPosition.x !== 0 || editorPosition.y !== 0 ? '600px' : 'auto', 
            zIndex: (draggingPane === 'editor'|| frontPane === 'editor') ? 1000 : 1
          }}
          onClick={() => setFrontPane('editor')}
        >
          <div 
            className="pane-header draggable-header"
            onMouseDown={(e) => handlePaneDragStart('editor', e)}
            style={{ cursor: 'move' }}
          >
            <h3>📝 편집기</h3>
            <div className="header-right">
              <span className="char-count">{markdown.length} 글자</span>
              <button 
                className="popup-btn" 
                onClick={openEditorInNewWindow}
                title="새 창으로 열기"
              >
                <Eye className="icon" />
              </button>
              <button 
                className="editor-copy-btn" 
                onClick={handleCopy}
                title="편집기 내용 복사"
              >
                {copied ? <Check className="icon" /> : <Copy className="icon" />}
              </button>
              <button 
                className="editor-clear-btn" 
                onClick={() => setMarkdown('')}
                title="편집기 내용 지우기"
              >
                <X className="icon" />
              </button>
              {/* <button 
                className="fullscreen-btn" 
                onClick={() => setIsEditorFullscreen(!isEditorFullscreen)}
                title={isEditorFullscreen ? "축소" : "전체화면"}
              >
                {isEditorFullscreen ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
              </button> */}
            </div>
          </div>
          <textarea
            className="markdown-input"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder="여기에 Markdown을 작성하세요..."
          />
        </div>
        )}

        {(activeTab === 'preview' || activeTab === 'both') && (
          <div 
            className={`preview-pane ${isPreviewFullscreen ? 'fullscreen' : ''} ${draggingPane === 'preview' ? 'dragging-pane' : ''}`}
            style={{
              position: previewPosition.x !== 0 || previewPosition.y !== 0 ? 'absolute' : 'static',
              left: previewPosition.x !== 0 ? `${previewPosition.x}px` : 'auto',
              top: previewPosition.y !== 0 ? `${previewPosition.y}px` : 'auto',
              width: previewPosition.x !== 0 || previewPosition.y !== 0 ? 'calc(50% - 10px)' : 'auto',
              height: previewPosition.x !== 0 || previewPosition.y !== 0 ? '600px' : 'auto',
              zIndex: (draggingPane === 'preview'|| frontPane === 'preview') ? 1000 : 1
            }}
            onClick={() => setFrontPane('preview')}
          >
            <div 
              className="pane-header draggable-header"
              onMouseDown={(e) => handlePaneDragStart('preview', e)}
              style={{ cursor: 'move' }}
            >
              <h3>👁️ 미리보기</h3>
              <div className="header-right">
                <button 
                  className="popup-btn" 
                  onClick={openPreviewInNewWindow}
                  title="새 창으로 열기"
                >
                  <Eye className="icon" />
                </button>
                <button 
                  className="preview-copy-btn" 
                  onClick={handleCopy}
                  title="내용 복사"
                >
                  {copied ? <Check className="icon" /> : <Copy className="icon" />}
                </button>
                <button 
                  className="tts-btn" 
                  onClick={handleTextToSpeech}
                  title={isSpeaking ? "읽기 중지" : "읽어주기"}
                >
                  {isSpeaking ? <VolumeX className="icon" /> : <Volume2 className="icon" />}
                </button>
                {/* <button 
                  className="fullscreen-btn" 
                  onClick={() => setIsPreviewFullscreen(!isPreviewFullscreen)}
                  title={isPreviewFullscreen ? "축소" : "전체화면"}
                >
                  {isPreviewFullscreen ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
                </button> */}
              </div>
            </div>
            <div 
              className="markdown-preview"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(markdown) }}
            />
          </div>
        )}
      </div>
      
      {/* 드래그 중일 때 표시할 오버레이 */}
      {isDragging && (
        <div className="drag-overlay">
          <p>여기에 Markdown 파일을 드롭하세요</p>
        </div>
      )}

      <div className="md-editor-footer">
        <div className="syntax-help">
          <strong>Markdown 문법:</strong>
          <span># 제목</span>
          <span>**굵게**</span>
          <span>*기울임*</span>
          <span>`코드`</span>
          <span>[링크](url)</span>
          <span>- 리스트</span>
        </div>
      </div>
    </div>
  );
};

export default MDEditor;